import React, { useState, useEffect } from "react";
import { Lock, Eye, EyeOff, Menu, X, ChevronRight, BookOpen, Users, Shield, MessageSquare, Zap, AlertTriangle, CheckCircle, Bot, Settings, Filter, List } from "lucide-react";

const GuidePage = () => {
  const [password, setPassword] = useState("");
  const [isUnlocked, setIsUnlocked] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState("Verifying...");
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [activeSection, setActiveSection] = useState("");
  const [isHidden, setIsHidden] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError("");
    setLoadingMessage("Verifying...");

    const slowTimer = setTimeout(() => {
      setLoadingMessage("Connecting to server... Please wait");
    }, 3000);

    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL || "";
      const response = await fetch(`${API_URL}/api/verify-guide-password`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ password }),
      });

      clearTimeout(slowTimer);

      if (response.ok) {
        setIsUnlocked(true);
        sessionStorage.setItem("guideUnlocked", "true");
      } else {
        setError("Invalid password. Please try again.");
      }
    } catch (err) {
      clearTimeout(slowTimer);
      setError("Unable to verify password. Please try again later.");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const unlocked = sessionStorage.getItem("guideUnlocked");
    if (unlocked === "true") {
      setIsUnlocked(true);
    }
  }, []);

  // Screenshot protection - hide content when window loses focus or PrintScreen is pressed
  useEffect(() => {
    if (!isUnlocked) return;

    // Hide content when window loses focus (common during screenshots)
    const handleVisibilityChange = () => {
      if (document.hidden) {
        setIsHidden(true);
      } else {
        // Small delay before showing content again
        setTimeout(() => setIsHidden(false), 100);
      }
    };

    // Hide content on blur (window loses focus)
    const handleBlur = () => {
      setIsHidden(true);
    };

    const handleFocus = () => {
      setTimeout(() => setIsHidden(false), 100);
    };

    // Detect PrintScreen and other screenshot keys
    const handleKeyDown = (e) => {
      // PrintScreen key
      if (e.key === 'PrintScreen' || e.keyCode === 44) {
        setIsHidden(true);
        navigator.clipboard.writeText('').catch(() => {});
        setTimeout(() => setIsHidden(false), 1000);
      }
      // Cmd+Shift+3 or Cmd+Shift+4 (Mac screenshots)
      if (e.metaKey && e.shiftKey && (e.key === '3' || e.key === '4')) {
        setIsHidden(true);
        setTimeout(() => setIsHidden(false), 1000);
      }
      // Windows+Shift+S (Windows Snipping Tool)
      if (e.metaKey && e.shiftKey && e.key === 's') {
        setIsHidden(true);
        setTimeout(() => setIsHidden(false), 1000);
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('blur', handleBlur);
    window.addEventListener('focus', handleFocus);
    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('keyup', handleKeyDown);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('blur', handleBlur);
      window.removeEventListener('focus', handleFocus);
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('keyup', handleKeyDown);
    };
  }, [isUnlocked]);

  // Track active section on scroll
  useEffect(() => {
    if (!isUnlocked) return;
    
    const handleScroll = () => {
      const sections = document.querySelectorAll('[data-section]');
      let currentSection = '';
      
      sections.forEach(section => {
        const rect = section.getBoundingClientRect();
        if (rect.top <= 150) {
          currentSection = section.getAttribute('data-section');
        }
      });
      
      setActiveSection(currentSection);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [isUnlocked]);

  const scrollToSection = (sectionId) => {
    const element = document.querySelector(`[data-section="${sectionId}"]`);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const navigation = [
    { id: 'tech-welcome', title: '1. Welcome Message', icon: MessageSquare, chapter: 2 },
    { id: 'tech-buybots', title: '2. Buybots Setup', icon: Bot, chapter: 2 },
    { id: 'tech-filters', title: '3. Filters', icon: Filter, chapter: 2 },
    { id: 'tech-blacklist', title: '4. Blacklisted Words', icon: Shield, chapter: 2 },
    { id: 'tech-useful', title: '5. More Useful Things', icon: Settings, chapter: 2 },
  ];

  // Blue info box component
  const InfoBox = ({ children }) => (
    <div className="mt-4 p-4 bg-cyan-400/10 border border-cyan-400/30 rounded-lg">
      <p className="text-cyan-400 text-sm italic">{children}</p>
    </div>
  );

  // Password Entry Screen
  if (!isUnlocked) {
    return (
      <div 
        className="min-h-screen flex items-center justify-center px-4"
        style={{
          background: 'linear-gradient(135deg, #0a0a0a 0%, #121212 50%, #0a0a0a 100%)',
        }}
      >
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div 
            className="absolute top-1/4 left-1/4 w-96 h-96 rounded-full opacity-10"
            style={{
              background: 'radial-gradient(circle, #00DAF0 0%, transparent 70%)',
              filter: 'blur(60px)',
            }}
          />
          <div 
            className="absolute bottom-1/4 right-1/4 w-80 h-80 rounded-full opacity-10"
            style={{
              background: 'radial-gradient(circle, #00DAF0 0%, transparent 70%)',
              filter: 'blur(60px)',
            }}
          />
        </div>

        <div className="relative z-10 w-full max-w-md">
          <div 
            className="p-8 rounded-lg border-2 border-gray-800"
            style={{
              background: 'rgba(18, 18, 18, 0.95)',
              boxShadow: '0 0 40px rgba(0, 0, 0, 0.5)',
            }}
          >
            <div className="flex justify-center mb-6">
              <div 
                className="w-16 h-16 rounded-full flex items-center justify-center"
                style={{
                  background: 'rgba(0, 218, 240, 0.1)',
                  border: '2px solid rgba(0, 218, 240, 0.3)',
                }}
              >
                <Lock size={32} style={{ color: '#00DAF0' }} />
              </div>
            </div>

            <h1 
              className="text-xl font-semibold text-center mb-2"
              style={{ color: '#00DAF0' }}
            >
              Protected Content
            </h1>

            <p className="text-gray-400 text-center text-sm mb-6">
              Enter the password provided by our team to unlock this content.
            </p>

            <form onSubmit={handleSubmit}>
              <div className="relative mb-4">
                <input
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter password"
                  className="w-full p-4 pr-12 border-2 border-gray-700 bg-gray-900 text-white rounded-lg focus:border-cyan-400 focus:outline-none transition-colors"
                  autoComplete="off"
                  autoFocus
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-cyan-400 transition-colors"
                >
                  {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>

              {error && (
                <p className="text-red-400 text-sm text-center mb-4">
                  {error}
                </p>
              )}

              <button
                type="submit"
                disabled={isLoading || !password}
                className="w-full py-3 rounded-lg font-semibold transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                style={{
                  background: 'linear-gradient(135deg, #00DAF0 0%, #00b8d4 100%)',
                  color: '#000',
                  boxShadow: isLoading ? 'none' : '0 0 20px rgba(0, 218, 240, 0.3)',
                }}
              >
                {isLoading ? loadingMessage : "Unlock Content"}
              </button>

              {isLoading && (
                <p className="text-gray-500 text-xs text-center mt-3">
                  First request may take up to 30 seconds while server wakes up
                </p>
              )}
            </form>
          </div>

          <p className="text-center text-gray-600 text-xs mt-6">
            TG4Crypto - Protected Guide
          </p>
        </div>
      </div>
    );
  }

  // Hidden screen for screenshot protection
  if (isHidden) {
    return (
      <div 
        className="min-h-screen flex items-center justify-center"
        style={{
          background: '#0a0a0a',
        }}
      >
        <div className="text-center">
          <Lock size={48} className="mx-auto mb-4 text-gray-700" />
          <p className="text-gray-600 text-sm">Content protected</p>
        </div>
      </div>
    );
  }

  // Unlocked Content
  return (
    <div 
      className="min-h-screen"
      style={{
        background: 'linear-gradient(135deg, #0a0a0a 0%, #121212 50%, #0a0a0a 100%)',
        userSelect: 'none',
        WebkitUserSelect: 'none',
        MozUserSelect: 'none',
        msUserSelect: 'none',
      }}
      onContextMenu={(e) => e.preventDefault()}
      onDragStart={(e) => e.preventDefault()}
    >
      {/* Mobile Menu Button */}
      <button
        onClick={() => setSidebarOpen(!sidebarOpen)}
        className="fixed top-4 left-4 z-50 lg:hidden p-2 rounded-lg bg-gray-800 text-cyan-400"
      >
        {sidebarOpen ? <X size={24} /> : <Menu size={24} />}
      </button>

      {/* Sidebar Navigation */}
      <aside
        className={`fixed left-0 top-0 h-full w-72 bg-gray-900/95 border-r border-gray-800 overflow-y-auto z-40 transform transition-transform duration-300 ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        } lg:translate-x-0`}
        style={{ backdropFilter: 'blur(10px)' }}
      >
        <div className="p-6">
          <h2 className="text-lg font-bold text-cyan-400 mb-2">TG4Crypto Guide</h2>
          <p className="text-xs text-gray-500 mb-6">Telegram Moderator Training</p>
          
          {/* Technical Instructions */}
          <div>
            <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">
              Technical Instructions
            </h3>
            <nav className="space-y-1">
              {navigation.map((item) => (
                <button
                  key={item.id}
                  onClick={() => scrollToSection(item.id)}
                  className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-left text-sm transition-all duration-200 ${
                    activeSection === item.id
                      ? 'bg-cyan-400/20 text-cyan-400'
                      : 'text-gray-400 hover:bg-gray-800 hover:text-white'
                  }`}
                >
                  <item.icon size={16} className="flex-shrink-0" />
                  <span className="truncate">{item.title}</span>
                </button>
              ))}
            </nav>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <main className={`transition-all duration-300 ${sidebarOpen ? 'lg:ml-72' : ''}`}>
        <div className="max-w-4xl mx-auto px-6 py-12 lg:py-16">
          
          {/* Header */}
          <div className="text-center mb-16">
            <h1 className="text-4xl font-bold text-white mb-4">
              Telegram Moderator Training Guide
            </h1>
            <div className="w-24 h-1 mx-auto rounded bg-gradient-to-r from-transparent via-cyan-400 to-transparent mb-6" />
          </div>

          {/* CHAPTER 2 */}
          <div className="mb-16">
            <div className="flex items-center gap-3 mb-8">
              <div className="w-12 h-12 rounded-lg bg-cyan-400/20 flex items-center justify-center">
                <Settings size={24} className="text-cyan-400" />
              </div>
              <h2 className="text-2xl font-bold text-white">Technical Instructions</h2>
            </div>

            {/* 1. Welcome Message */}

            <section data-section="tech-welcome" className="mb-12 scroll-mt-8">
              <h3 className="text-xl font-semibold text-cyan-400 mb-4 flex items-center gap-2">
                <span className="text-gray-500">1)</span> Welcome Message
              </h3>
              <p className="text-gray-300 mb-4">For this, we'll be using <strong className="text-white">Safeguard</strong>:</p>
              
              <ol className="space-y-3 text-gray-300 mb-6">
                {[
                  "Open Safeguard's menu by going to the Telegram group chat and executing the following command: /config@Safeguard",
                  'Click on "Open in private chat"',
                  'Click on "Greetings"',
                  'Click on "Change Text"',
                  'Insert the preferred welcome message and hit "Enter" - example: "Welcome {mention} to GROUPNAME (...)"',
                  'Click on "Add Media" and send image/GIF that the welcome message should contain',
                  'Click on "Edit Buttons"'
                ].map((step, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <span className="w-6 h-6 rounded-full bg-cyan-400/20 flex items-center justify-center text-cyan-400 text-sm font-semibold flex-shrink-0">
                      {i + 1}
                    </span>
                    <span>{step}</span>
                  </li>
                ))}
              </ol>

              <div className="bg-gray-800/50 border border-gray-700 rounded-lg p-4 mb-4">
                <p className="text-white font-semibold mb-2">Button Syntax:</p>
                <code className="text-cyan-400 text-sm block mb-2">[NAME](LINK)</code>
                <p className="text-gray-400 text-sm">Creates a button called name pointing to link on a new line</p>
                <code className="text-cyan-400 text-sm block mb-2 mt-2">[NAME](LINK:same)</code>
                <p className="text-gray-400 text-sm">Creates a button on the same line as the button(s) above it</p>
              </div>

              <div className="bg-gray-800/50 border border-gray-700 rounded-lg p-4">
                <p className="text-white font-semibold mb-2">Example:</p>
                <code className="text-cyan-400 text-sm">[Chart](dextools.io) [Twitter](twitter.com) [Website](project.com:same)</code>
              </div>

              <p className="text-gray-300 mt-4 mb-2">The following buttons should be added to the welcome message:</p>
              <ul className="grid sm:grid-cols-2 gap-2 text-gray-400 text-sm">
                <li>• Website</li>
                <li>• Chart (Dextools or Dexscreener)</li>
                <li>• Twitter</li>
                <li>• CoinMarketCap (if live already)</li>
                <li>• CoinGecko (if live already)</li>
                <li>• ... as well as other useful URLs</li>
              </ul>
            </section>

            {/* 2. Buybots Setup */}
            <section data-section="tech-buybots" className="mb-12 scroll-mt-8">
              <h3 className="text-xl font-semibold text-cyan-400 mb-4 flex items-center gap-2">
                <span className="text-gray-500">2)</span> Buybots Setup
              </h3>

              {/* Ethereum */}
              <div className="mb-8">
                <h4 className="text-lg font-semibold text-white mb-3 flex items-center gap-2">
                  <span className="px-2 py-1 bg-blue-900/50 text-blue-400 text-xs rounded">2.1</span>
                  Ethereum
                </h4>
                <p className="text-gray-300 mb-3">For ETH Chain, we will be using:</p>
                <div className="flex gap-2 mb-4">
                  <span className="px-3 py-1 bg-gray-800 text-cyan-400 rounded text-sm">@Safeguard</span>
                  <span className="px-3 py-1 bg-gray-800 text-cyan-400 rounded text-sm">@buytech</span>
                </div>

                <div className="bg-gray-800/30 border border-gray-700 rounded-lg p-4 mb-4">
                  <p className="text-white font-semibold mb-3">Setup @Safeguard as buybot:</p>
                  <ol className="space-y-2 text-gray-400 text-sm">
                    <li>1. Send <code className="text-cyan-400">/add@safeguard</code> command into the Telegram group chat</li>
                    <li>2. Click "Add Token" → Select "Ethereum"</li>
                    <li>3. Paste the respective contract address and press enter</li>
                    <li>4. Select the desired pair (check which DEX/aggregator has the most volume)</li>
                    <li>5. Click "Gif / Media" and send the desired GIF or picture for new buys</li>
                    <li>6. Click "Min Buy $15" and specify a minimum buy amount</li>
                    <li>7. Click "Emoji" and set the desired emoji (custom emojis supported!)</li>
                    <li>8. Click "Step $20" and specify a buy step amount</li>
                    <li>9. Toggle "Show Price" on/off as needed (useful if market cap token supply is not a round number)</li>
                    <li>10. Toggle "Whale Alerts" on/off (if needed, specify circulating supply via "Circ Supply Not Set")</li>
                    <li>11. Click "Add Buttons" → "+ Add" to set up to 3 buttons using syntax: Button Name, then Button Link</li>
                    <li>12. Click "Buy Layouts" and choose the desired layout (Layout 2 recommended)</li>
                  </ol>
                </div>

                <div className="bg-gray-800/30 border border-gray-700 rounded-lg p-4 mb-4">
                  <p className="text-white font-semibold mb-3">Setup @buytech as buybot:</p>
                  <ol className="space-y-2 text-gray-400 text-sm">
                    <li>1. Add @buytech to the desired group as a member</li>
                    <li>2. Promote with (default) admin permissions</li>
                    <li>3. Execute: <code className="text-cyan-400">/add@buytech</code></li>
                    <li>4. Select "ETH"</li>
                    <li>5. Reply with the token contract address</li>
                    <li>6. Select the desired pair (the one with the most volume)</li>
                    <li>7. Click "Buybot settings"</li>
                    <li>8. Click "Gif / Image" and send the desired media</li>
                    <li>9. Click "Min Buy $0" and specify a minimum amount</li>
                    <li>10. Click "Buy Emoji" and send the desired emojis (custom emojis supported!)</li>
                    <li>11. Click "Buy Step $10" and specify a buy step</li>
                    <li>12. Click "Group Settings"</li>
                    <li>13. Toggle "Notify Whale Buy's" on/off as needed</li>
                    <li>14. Click "Selected Chart: Geckoterminal" and change to "DexTools"</li>
                    <li>15. If needed, specify circulating supply via "C.Supply : XXXX"</li>
                  </ol>
                </div>

                <div className="bg-red-900/20 border border-red-700/30 rounded-lg p-4">
                  <p className="text-red-400 font-semibold mb-2">⚠️ Important:</p>
                  <p className="text-gray-300 text-sm">Safeguard automatically kicks @buytech when adding it. You need to: take away Safeguard's "Ban users" permission via Telegram members menu → add @buytech to the group → then re-enable Safeguard's "Ban users" permission.</p>
                </div>
              </div>

              {/* Solana */}
              <div className="mb-8">
                <h4 className="text-lg font-semibold text-white mb-3 flex items-center gap-2">
                  <span className="px-2 py-1 bg-purple-900/50 text-purple-400 text-xs rounded">2.2</span>
                  Solana
                </h4>
                <p className="text-gray-300 mb-3">For SOL Chain, we will be using:</p>
                <div className="flex gap-2 mb-4">
                  <span className="px-3 py-1 bg-gray-800 text-cyan-400 rounded text-sm">@Safeguard</span>
                  <span className="px-3 py-1 bg-gray-800 text-cyan-400 rounded text-sm">@delugebuybot</span>
                </div>

                <div className="bg-gray-800/30 border border-gray-700 rounded-lg p-4 mb-4">
                  <p className="text-white font-semibold mb-3">Setup @Safeguard as buybot:</p>
                  <ol className="space-y-2 text-gray-400 text-sm">
                    <li>1. Send <code className="text-cyan-400">/add@safeguard</code> command into the Telegram group chat</li>
                    <li>2. Click "Add Token" → Select "Solana"</li>
                    <li>3. Reply with the contract address</li>
                    <li>4. Select the desired pair (check which DEX/aggregator has the most volume)</li>
                    <li>5. Click "Gif / Media" and send the desired GIF or picture for new buys</li>
                    <li>6. Click "Min Buy $15" and specify a minimum buy amount by replying with a number</li>
                    <li>7. Click "Emoji" and set the desired emoji (custom emojis supported!)</li>
                    <li>8. Click "Step $20" and specify a buy step amount</li>
                    <li>9. Toggle "Show Price" on/off as needed</li>
                    <li>10. Toggle "Whale Alerts" on/off (if needed, specify circulating supply via "Circ. Supply not set")</li>
                    <li>11. Click "Add Buttons" → "+ Add" to set up to 3 buttons</li>
                    <li>12. Click "Buy Layouts" and choose the desired layout (Layout 2 recommended)</li>
                  </ol>
                </div>

                <div className="bg-gray-800/30 border border-gray-700 rounded-lg p-4 mb-4">
                  <p className="text-white font-semibold mb-3">Setup @delugebuybot as buybot:</p>
                  <ol className="space-y-2 text-gray-400 text-sm">
                    <li>1. Add @delugebuybot to the group as a member</li>
                    <li>2. Promote with (default) admin permissions</li>
                    <li>3. Execute: <code className="text-cyan-400">/add@delugebuybot</code></li>
                    <li>4. Select "Continue in a private chat"</li>
                    <li>5. Click "Continue in a private chat" (again)</li>
                    <li>6. Send the contract address</li>
                    <li>7. Reply with the group or portal link</li>
                    <li>8. Click "BuyBot Settings"</li>
                    <li>9. Configure options as desired (same as Safeguard)</li>
                    <li>10. Make sure "Shuffle: 🔴" is OFF and "Market Cap: 🟢" is ON</li>
                  </ol>
                </div>

                <div className="bg-yellow-900/20 border border-yellow-700/30 rounded-lg p-4">
                  <p className="text-yellow-400 font-semibold mb-2">⚠️ Important:</p>
                  <p className="text-gray-300 text-sm">After setting up @delugebuybot, toggle off the welcome message which is enabled by default. Click "Back to group settings" → "Portal/Group Settings" → toggle off "Welcome: 🟢"</p>
                </div>
              </div>

              {/* BSC */}
              <div className="mb-8">
                <h4 className="text-lg font-semibold text-white mb-3 flex items-center gap-2">
                  <span className="px-2 py-1 bg-yellow-900/50 text-yellow-400 text-xs rounded">2.3</span>
                  Binance Smart Chain
                </h4>
                <p className="text-gray-300 mb-3">For BNB Chain, we will be using:</p>
                <div className="flex gap-2 mb-4">
                  <span className="px-3 py-1 bg-gray-800 text-cyan-400 rounded text-sm">@Safeguard</span>
                  <span className="px-3 py-1 bg-gray-800 text-cyan-400 rounded text-sm">@buytech</span>
                </div>

                <div className="bg-gray-800/30 border border-gray-700 rounded-lg p-4 mb-4">
                  <p className="text-white font-semibold mb-3">Setup @Safeguard as buybot:</p>
                  <ol className="space-y-2 text-gray-400 text-sm">
                    <li>1. Send <code className="text-cyan-400">/add@safeguard</code> command into the Telegram group chat</li>
                    <li>2. Click "Add Token" → Select "Binance"</li>
                    <li>3. Reply with the contract address</li>
                    <li>4. Select the desired pair (check which DEX/aggregator has the most volume)</li>
                    <li>5. Click "Gif / Media" and send the desired GIF or picture for new buys</li>
                    <li>6. Click "Min Buy $15" and specify a minimum buy amount by replying with a number</li>
                    <li>7. Click "Emoji" and set the desired emoji (custom emojis supported!)</li>
                    <li>8. Click "Step $20" and specify a buy step amount</li>
                    <li>9. Toggle "Show Price" on/off as needed</li>
                    <li>10. Toggle "Whale Alerts" on/off (if needed, specify circulating supply)</li>
                    <li>11. Click "Add Buttons" → "+ Add" to set up to 3 buttons</li>
                    <li>12. Click "Buy Layouts" and choose the desired layout (Layout 2 recommended)</li>
                  </ol>
                </div>

                <div className="bg-gray-800/30 border border-gray-700 rounded-lg p-4 mb-4">
                  <p className="text-white font-semibold mb-3">Setup @buytech as buybot:</p>
                  <ol className="space-y-2 text-gray-400 text-sm">
                    <li>1. Add @buytech to the desired group as a member</li>
                    <li>2. Promote with (default) admin permissions</li>
                    <li>3. Execute: <code className="text-cyan-400">/add@buytech</code></li>
                    <li>4. Select "ETH" (also works for BSC)</li>
                    <li>5. Reply with the token contract address</li>
                    <li>6. Select the desired pair (the one with the most volume)</li>
                    <li>7. Click "Buybot settings"</li>
                    <li>8. Click "Gif / Image" and send the desired media</li>
                    <li>9. Click "Min Buy $0" and specify a minimum amount</li>
                    <li>10. Click "Buy Emoji" and send the desired emojis (custom emojis supported!)</li>
                    <li>11. Click "Buy Step $10" and specify a buy step</li>
                    <li>12. Click "Group Settings"</li>
                    <li>13. Toggle "Notif Whale Buy's" on/off as needed</li>
                    <li>14. Click "Selected Chart: Geckoterminal" and change to "DexTools"</li>
                    <li>15. If needed, specify circulating supply via "C.Supply : XXXX"</li>
                  </ol>
                </div>

                <div className="bg-red-900/20 border border-red-700/30 rounded-lg p-4">
                  <p className="text-red-400 font-semibold mb-2">⚠️ Important:</p>
                  <p className="text-gray-300 text-sm">Safeguard automatically kicks @buytech when adding it. You need to: take away Safeguard's "Ban users" permission via Telegram members menu → add @buytech to the group → then re-enable Safeguard's "Ban users" permission.</p>
                </div>
              </div>

              {/* SUI */}
              <div className="mb-4">
                <h4 className="text-lg font-semibold text-white mb-3 flex items-center gap-2">
                  <span className="px-2 py-1 bg-cyan-900/50 text-cyan-400 text-xs rounded">2.4</span>
                  Sui
                </h4>
                <p className="text-gray-300 mb-3">For SUI chain, we will be using:</p>
                <div className="flex gap-2 mb-4">
                  <span className="px-3 py-1 bg-gray-800 text-cyan-400 rounded text-sm">@s_buybot</span>
                  <span className="px-3 py-1 bg-gray-800 text-cyan-400 rounded text-sm">@bullsharkbuybot</span>
                </div>
                
                <div className="bg-gray-800/30 border border-gray-700 rounded-lg p-4 mb-4">
                  <p className="text-white font-semibold mb-3">Setup @s_buybot:</p>
                  <ol className="space-y-2 text-gray-400 text-sm">
                    <li>1. Add @s_buybot to the group as a member</li>
                    <li>2. Promote with (default) admin permissions</li>
                    <li>3. Execute: <code className="text-cyan-400">/start@s_buybot</code></li>
                    <li>4. Click "Start Config in Private Chat"</li>
                    <li>5. Send the token contract address</li>
                    <li>6. Specify an emoji by simply sending it (custom emojis supported)</li>
                    <li>7. Reply with the group or portal link</li>
                    <li>8. Send the buybot GIF/image</li>
                    <li>9. Set "Min Buy" amount, "Emoji Buy Size", and "Total Supply: XXX" (if needed)</li>
                    <li>10. Set a buy link by clicking "Buy Link" and sending the URL</li>
                    <li>11. Toggle desired liquidity pools on/off by clicking their buttons</li>
                    <li>12. You can add custom LPs via "Add Pool" (supports Bluemove, Turbos, Cetus, FlowX)</li>
                  </ol>
                </div>

                <div className="bg-gray-800/30 border border-gray-700 rounded-lg p-4">
                  <p className="text-white font-semibold mb-3">Setup @bullsharkbuybot:</p>
                  <ol className="space-y-2 text-gray-400 text-sm">
                    <li>1. Add @bullsharkbuybot to the group as a member</li>
                    <li>2. Promote with (default) admin permissions</li>
                    <li>3. Execute: <code className="text-cyan-400">/start@bullsharkbuybot</code></li>
                    <li>4. Click "Click me"</li>
                    <li>5. Select "+ Add Token"</li>
                    <li>6. Choose "SUI"</li>
                    <li>7. Send the project's contract address</li>
                    <li>8. Set "Gif / Image" and "Buy Emoji" (custom emojis supported)</li>
                    <li>9. If needed, specify "Buy Step", "Min. Buy", and "Cir. Supply:"</li>
                    <li>10. Specify "Telegram Link", "X Link", and "Website Link"</li>
                  </ol>
                </div>
              </div>
            </section>

            {/* 3. Filters */}
            <section data-section="tech-filters" className="mb-12 scroll-mt-8">
              <h3 className="text-xl font-semibold text-cyan-400 mb-4 flex items-center gap-2">
                <span className="text-gray-500">3)</span> Filters
              </h3>
              <p className="text-gray-300 mb-4">The best practice is to prepare the message with desired formatting in advance.</p>

              <div className="grid md:grid-cols-2 gap-4 mb-6">
                <div className="bg-gray-800/30 border border-gray-700 rounded-lg p-4">
                  <h4 className="text-white font-semibold mb-3">Save a filter:</h4>
                  <p className="text-gray-400 text-sm mb-2"><strong className="text-white">Rose:</strong></p>
                  <code className="text-cyan-400 text-sm block mb-3">/filter@missrose_bot /filtername</code>
                  <p className="text-gray-400 text-sm mb-2"><strong className="text-white">Safeguard:</strong></p>
                  <code className="text-cyan-400 text-sm block">/filter@safeguard /filtername</code>
                </div>
                
                <div className="bg-gray-800/30 border border-gray-700 rounded-lg p-4">
                  <h4 className="text-white font-semibold mb-3">Delete a filter:</h4>
                  <p className="text-gray-400 text-sm mb-2"><strong className="text-white">Rose:</strong></p>
                  <code className="text-cyan-400 text-sm block mb-3">/stop@missrose_bot /filtername</code>
                  <p className="text-gray-400 text-sm mb-2"><strong className="text-white">Safeguard:</strong></p>
                  <code className="text-cyan-400 text-sm block">/remove@safeguard /filtername</code>
                </div>
              </div>

              <div className="bg-gray-800/30 border border-gray-700 rounded-lg p-4 mb-6">
                <p className="text-white font-semibold mb-2">Note about filters with spaces:</p>
                <p className="text-gray-400 text-sm mb-2">Only Rose supports filternames containing spaces. Use this syntax:</p>
                <code className="text-cyan-400 text-sm">/filter@missrose_bot "These words are a filtername"</code>
              </div>

              <h4 className="text-white font-semibold mb-3">Default filters to configure:</h4>
              <div className="grid sm:grid-cols-2 gap-2">
                {[
                  { cmd: "/ca", desc: "Contract address" },
                  { cmd: "Ca", desc: "Contract address" },
                  { cmd: "/twitter", desc: "X account" },
                  { cmd: "/X", desc: "X account" },
                  { cmd: "/chart", desc: "Dext / Dexscr / etc." },
                  { cmd: "/buy", desc: "Direct buy link" },
                ].map((filter, i) => (
                  <div key={i} className="flex items-center gap-2 bg-gray-800/50 rounded px-3 py-2">
                    <code className="text-cyan-400 text-sm">{filter.cmd}</code>
                    <span className="text-gray-500">→</span>
                    <span className="text-gray-400 text-sm">{filter.desc}</span>
                  </div>
                ))}
              </div>
            </section>

            {/* 4. Blacklisted Words */}
            <section data-section="tech-blacklist" className="mb-12 scroll-mt-8">
              <h3 className="text-xl font-semibold text-cyan-400 mb-4 flex items-center gap-2">
                <span className="text-gray-500">4)</span> Blacklisted Words
              </h3>
              <p className="text-gray-300 mb-4">Use @missrose_bot with the following syntax:</p>

              <div className="bg-gray-800/30 border border-gray-700 rounded-lg p-4 mb-4">
                <p className="text-white font-semibold mb-2">Add to blocklist:</p>
                <code className="text-cyan-400 text-sm block mb-2">/addblocklist &lt;trigger&gt; &lt;reason&gt;</code>
                <p className="text-gray-400 text-sm">Examples:</p>
                <ul className="text-gray-400 text-sm mt-2 space-y-1">
                  <li><code className="text-cyan-400">/addblocklist bio</code> - adds "bio"</li>
                  <li><code className="text-cyan-400">/addblocklist bio spam</code> - adds "bio" with reason "spam"</li>
                  <li><code className="text-cyan-400">/addblocklist "check my bio"</code> - adds phrase</li>
                  <li><code className="text-cyan-400">/addblocklist "check my bio" spam</code> - adds phrase with reason</li>
                </ul>
              </div>

              <div className="bg-gray-800/30 border border-gray-700 rounded-lg p-4">
                <p className="text-white font-semibold mb-2">Remove from blocklist:</p>
                <code className="text-cyan-400 text-sm block mb-2">/rmblocklist &lt;trigger&gt;</code>
                <p className="text-gray-400 text-sm">Examples:</p>
                <ul className="text-gray-400 text-sm mt-2 space-y-1">
                  <li><code className="text-cyan-400">/rmblocklist bio</code></li>
                  <li><code className="text-cyan-400">/rmblocklist "check my bio"</code></li>
                </ul>
              </div>
            </section>

            {/* 5. More Useful Things */}
            <section data-section="tech-useful" className="mb-12 scroll-mt-8">
              <h3 className="text-xl font-semibold text-cyan-400 mb-4 flex items-center gap-2">
                <span className="text-gray-500">5)</span> More Useful Things
              </h3>
              
              <div className="bg-yellow-900/20 border border-yellow-700/30 rounded-lg p-4">
                <p className="text-yellow-400 font-semibold mb-2">⚠️ Safeguard AI Misbehaving?</p>
                <p className="text-gray-300 text-sm mb-2">If Safeguard's AI function is misbehaving (i.e. by muting normal users using specific words), use:</p>
                <code className="text-cyan-400">/clearaiban@safeguard</code>
              </div>
            </section>
          </div>

          {/* Footer */}
          <div className="text-center pt-8 border-t border-gray-800">
            <p className="text-gray-500 text-sm">
              This content is confidential and intended only for authorized users.
            </p>
            <p className="text-gray-600 text-xs mt-2">
              © TG4Crypto - Telegram Moderator Training Guide
            </p>
          </div>
        </div>
      </main>
    </div>
  );
};

export default GuidePage;
